// Resvägar: the route each person of a company took on one day, derived from
// the service logs. Nothing here is stored.

/** `buildingServices/servicesLogs`: {year: {serviceType: {buildingKey: {logKey: log}}}} */
export type ServicesLogs = Record<string, unknown>;

export interface RouteStop {
    buildingKey: string;
    /** First log of the first visit, `YYYY-MM-DD hh:mm:ss` */
    start: string;
    /** Last log of the first visit */
    end: string;
    /** Service type keys done at the building that day, in log order, each once */
    services: string[];
    /** From the previous stop's first-visit end to this stop's start; absent on the first stop */
    minutesSincePrevious?: number;
}

export interface PersonRoute {
    uid: string;
    stops: RouteStop[];
}

interface RouteLog {
    buildingKey: string;
    serviceType: string;
    user: string;
    dateString: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const DATE_STRING = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/;

/**
 * The logs of the given years by members at placed buildings. A log without a
 * user or a well-formed dateString is skipped.
 */
const collectLogs = (
    servicesLogs: ServicesLogs | undefined,
    years: string[] | null,
    memberUids: ReadonlySet<string>,
    placedBuildingKeys: ReadonlySet<string>,
): RouteLog[] => {
    if (!servicesLogs) return [];
    const logs: RouteLog[] = [];
    const yearKeys = years ?? Object.keys(servicesLogs);
    yearKeys.forEach(year => {
        const yearLogs = servicesLogs[year];
        if (!isRecord(yearLogs)) return;
        Object.entries(yearLogs).forEach(([serviceType, byBuilding]) => {
            if (!isRecord(byBuilding)) return;
            Object.entries(byBuilding).forEach(([buildingKey, byLog]) => {
                if (!placedBuildingKeys.has(buildingKey) || !isRecord(byLog)) return;
                Object.values(byLog).forEach(log => {
                    if (!isRecord(log)) return;
                    const { user, dateString } = log;
                    if (typeof user !== 'string' || !memberUids.has(user)) return;
                    if (typeof dateString !== 'string' || !DATE_STRING.test(dateString)) return;
                    logs.push({ buildingKey, serviceType, user, dateString });
                });
            });
        });
    });
    return logs;
};

const dayOf = (dateString: string): string => dateString.slice(0, 10);

/** Seconds since midnight of a `YYYY-MM-DD hh:mm[:ss]` string */
export const secondsOfDay = (dateString: string): number => {
    const [h, m, s] = dateString.slice(11).split(':').map(Number);
    return h * 3600 + m * 60 + (s || 0);
};

/** Whole minutes from `from` to `to`, both on the same day */
export const minutesBetween = (from: string, to: string): number =>
    Math.round((secondsOfDay(to) - secondsOfDay(from)) / 60);

/** The days (`YYYY-MM-DD`, ascending) on which a member logged at a placed building */
export const daysWithLogs = (
    servicesLogs: ServicesLogs | undefined,
    memberUids: ReadonlySet<string>,
    placedBuildingKeys: ReadonlySet<string>,
): string[] => {
    const days = new Set(
        collectLogs(servicesLogs, null, memberUids, placedBuildingKeys).map(log => dayOf(log.dateString)),
    );
    return Array.from(days).sort();
};

/**
 * One route per member who logged on `day`, ordered by their first log. A
 * stop is one building, placed and timed by the first visit (the first run of
 * consecutive logs there); later visits the same day only add their services.
 */
export const buildRoutes = (
    servicesLogs: ServicesLogs | undefined,
    day: string,
    memberUids: ReadonlySet<string>,
    placedBuildingKeys: ReadonlySet<string>,
): PersonRoute[] => {
    const logs = collectLogs(servicesLogs, [day.slice(0, 4)], memberUids, placedBuildingKeys)
        .filter(log => dayOf(log.dateString) === day);

    const byUser = new Map<string, RouteLog[]>();
    logs.forEach(log => {
        const list = byUser.get(log.user) ?? [];
        list.push(log);
        byUser.set(log.user, list);
    });

    const routes: PersonRoute[] = [];
    byUser.forEach((userLogs, uid) => {
        // Same time at two buildings: keep each building's logs together
        userLogs.sort((a, b) =>
            a.dateString.localeCompare(b.dateString) || a.buildingKey.localeCompare(b.buildingKey));

        const stops: RouteStop[] = [];
        const stopByBuilding = new Map<string, RouteStop>();
        let runBuilding: string | null = null;
        let inFirstVisit = false;

        userLogs.forEach(log => {
            const startsRun = log.buildingKey !== runBuilding;
            runBuilding = log.buildingKey;
            let stop = stopByBuilding.get(log.buildingKey);
            if (!stop) {
                stop = { buildingKey: log.buildingKey, start: log.dateString, end: log.dateString, services: [] };
                stopByBuilding.set(log.buildingKey, stop);
                stops.push(stop);
                inFirstVisit = true;
            } else if (startsRun) {
                inFirstVisit = false;
            }
            if (inFirstVisit) stop.end = log.dateString;
            if (!stop.services.includes(log.serviceType)) stop.services.push(log.serviceType);
        });

        stops.forEach((stop, index) => {
            if (index > 0) stop.minutesSincePrevious = minutesBetween(stops[index - 1].end, stop.start);
        });
        routes.push({ uid, stops });
    });

    return routes.sort((a, b) => a.stops[0].start.localeCompare(b.stops[0].start) || a.uid.localeCompare(b.uid));
};

/** The nearest day with logs before (-1) or after (+1) `day`, or null at the end */
export const adjacentDay = (days: string[], day: string, direction: -1 | 1): string | null => {
    if (direction < 0) {
        for (let i = days.length - 1; i >= 0; i--) if (days[i] < day) return days[i];
        return null;
    }
    return days.find(d => d > day) ?? null;
};

/** `hh:mm` of a `YYYY-MM-DD hh:mm:ss` string */
export const timeOf = (dateString: string): string => dateString.slice(11, 16);

// Playback: the day replayed at a constant clock rate, the dot resting at a
// stop for its first visit and moving in a straight line between stops.

export type LatLng = [number, number];

interface Keyframe {
    /** Seconds since midnight */
    at: number;
    point: LatLng;
    stopIndex: number;
}

export interface RouteTimeline {
    keyframes: Keyframe[];
    /** First stop's start and last stop's end, seconds since midnight */
    from: number;
    to: number;
    /** Along the straight lines */
    distanceKm: number;
}

export interface PlaybackFrame {
    /** Where the person is at this moment */
    point: LatLng;
    /** The route drawn so far, ending at `point` */
    path: LatLng[];
    /** Stops arrived at so far */
    reachedStops: number;
    /** The stop the person is at, null while travelling */
    atStop: number | null;
    /** The clock, seconds since midnight */
    seconds: number;
}

const EARTH_RADIUS_KM = 6371;
const toRad = (deg: number) => (deg * Math.PI) / 180;

/** Great-circle distance */
export const distanceKm = ([lat1, lng1]: LatLng, [lat2, lng2]: LatLng): number => {
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
};

/** The route's keyframes: each stop's arrival and departure (first visit), in order */
export const routeTimeline = (stops: RouteStop[], pointOf: (buildingKey: string) => LatLng | undefined): RouteTimeline | null => {
    const keyframes: Keyframe[] = [];
    let km = 0;
    stops.forEach((stop, stopIndex) => {
        const point = pointOf(stop.buildingKey);
        if (!point) return;
        const previous = keyframes[keyframes.length - 1];
        if (previous) km += distanceKm(previous.point, point);
        keyframes.push({ at: secondsOfDay(stop.start), point, stopIndex });
        keyframes.push({ at: secondsOfDay(stop.end), point, stopIndex });
    });
    if (keyframes.length === 0) return null;
    return { keyframes, from: keyframes[0].at, to: keyframes[keyframes.length - 1].at, distanceKm: km };
};

const samePoint = (a: LatLng, b: LatLng) => a[0] === b[0] && a[1] === b[1];

/** The route at `progress` (0–1) of its time span; the clock moves linearly */
export const frameAt = (timeline: RouteTimeline, progress: number): PlaybackFrame => {
    const { keyframes, from, to } = timeline;
    const clamped = Math.min(1, Math.max(0, progress));
    const seconds = from + (to - from) * clamped;

    const path: LatLng[] = [];
    const push = (point: LatLng) => {
        if (path.length === 0 || !samePoint(path[path.length - 1], point)) path.push(point);
    };

    let point = keyframes[0].point;
    let atStop: number | null = keyframes[0].stopIndex;
    let reachedStops = 1;
    for (let i = 0; i < keyframes.length; i++) {
        const frame = keyframes[i];
        if (frame.at > seconds && i > 0) {
            // Between keyframe i-1 and i: resting when both are the same stop, else travelling
            const previous = keyframes[i - 1];
            if (previous.stopIndex === frame.stopIndex) {
                point = frame.point;
                atStop = frame.stopIndex;
            } else {
                const t = (seconds - previous.at) / (frame.at - previous.at);
                point = [
                    previous.point[0] + (frame.point[0] - previous.point[0]) * t,
                    previous.point[1] + (frame.point[1] - previous.point[1]) * t,
                ];
                atStop = null;
            }
            break;
        }
        push(frame.point);
        point = frame.point;
        atStop = frame.stopIndex;
        reachedStops = frame.stopIndex + 1;
    }
    push(point);
    return { point, path, reachedStops, atStop, seconds };
};

const MIN_PLAYBACK_MS = 6000;
const MAX_PLAYBACK_MS = 45000;

/**
 * How long the replay lasts: longer for a long day or a long drive, so each
 * plays at a pace that can be followed, within 6–45 seconds.
 */
export const playbackDurationMs = (timeline: RouteTimeline): number => {
    const hours = (timeline.to - timeline.from) / 3600;
    const ms = MIN_PLAYBACK_MS + hours * 2500 + timeline.distanceKm * 150;
    return Math.round(Math.min(MAX_PLAYBACK_MS, Math.max(MIN_PLAYBACK_MS, ms)));
};

/** `hh:mm` of seconds since midnight */
export const clockOf = (seconds: number): string => {
    const total = Math.floor(seconds / 60);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};
